import "./Logistics.css";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import { toast } from "react-toastify";

const safeParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const formatDateForInput = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const createEmptyStockItem = () => ({
  stockId: "",
  quantity: "",
  originalQuantity: 0,
  isDirty: false,
});

const buildInitialFormData = (stockout) => {
  if (stockout) {
    return {
      employeeId: stockout.employeeId?._id || "",
      employeeSearch: stockout.employeeId?.name || "",
      locationId: stockout.locationId?._id || "",
      date: formatDateForInput(stockout.date),
      stockItems:
        stockout.stocks && stockout.stocks.length > 0
          ? stockout.stocks.map((item) => ({
              stockId: item.stock?._id || "",
              quantity: item.quantity || "",
              originalQuantity: item.quantity || 0,
              isDirty: false,
            }))
          : [createEmptyStockItem()],
      remarks: stockout.remarks || "",
    };
  }

  return {
    employeeId: "",
    employeeSearch: "",
    locationId: "",
    date: formatDateForInput(new Date()),
    stockItems: [createEmptyStockItem()],
    remarks: "",
  };
};

const StockOutForm = ({ selectedCompany, companyId, stockout, onClose }) => {
  const authData =
    safeParse(localStorage.getItem("authData")) ||
    safeParse(sessionStorage.getItem("authData"));
  const currentUserId = authData?._id;

  const [employees, setEmployees] = useState([]);
  const [location, setLocation] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [locSearch, setLocSearch] = useState(() => {
    if (stockout?.locationId?.code && stockout?.locationId?.yard_name) {
      return `${stockout.locationId.code} - ${stockout.locationId.yard_name}`;
    }
    return "";
  });
  const [showLocDropdown, setShowLocDropdown] = useState(false);
  const [stockSearch, setStockSearch] = useState("");
  const [showStockDropdown, setShowStockDropdown] = useState({});
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(stockout),
  );

  // Helper to close all dropdowns at once
  const closeAllDropdowns = () => {
    setShowEmpDropdown(false);
    setShowLocDropdown(false);
    setShowStockDropdown({});
  };

  useEffect(() => {
    setFormData(buildInitialFormData(stockout));
  }, [stockout]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get(
          `/logistics/stockout/company/${companyId}`,
        );
        setEmployees(response.data.data || []);
      } catch (error) {
        toast.error("Error fetching employees");
      }
    };

    if (companyId) fetchEmployees();
  }, [companyId]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await axiosInstance.get(`/location`);
        setLocation(response.data.data || []);
      } catch (error) {
        toast.error("Error fetching location");
      }
    };

    fetchLocation();
  }, []);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await axiosInstance.get(
          `/logistics/stock/${companyId}`,
        );
        setStockList(response.data.stocks || []);
      } catch (error) {
        toast.error("Error fetching stocks");
      }
    };

    if (companyId) fetchStocks();
  }, [companyId]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStockItemChange = (index, field, value) => {
    const updatedItems = [...formData.stockItems];
    const currentRow = updatedItems[index];

    // Quantity change
    if (field === "quantity") {
      const currentStock = stockList.find((s) => s._id === currentRow.stockId);
      // Stock-out reduces from Godown B
      const currentAvailable = Number(currentStock?.stockBQuantity || 0);
      const originalQty = Number(currentRow.originalQuantity || 0);

      // In edit mode, allow new qty up to current stock + original qty
      const maxAllowed = stockout
        ? currentAvailable + originalQty
        : currentAvailable;

      // allow empty value while typing
      if (value !== "") {
        const qty = Number(value);

        if (Number.isNaN(qty) || qty < 1) {
          updatedItems[index] = {
            ...currentRow,
            quantity: value,
            isDirty: stockout ? true : currentRow.isDirty,
          };
          setFormData((prev) => ({ ...prev, stockItems: updatedItems }));
          return;
        }

        if (qty > maxAllowed) {
          toast.error(
            `Quantity cannot exceed available stock (${maxAllowed}).`,
          );
          return;
        }
      }

      updatedItems[index] = {
        ...currentRow,
        quantity: value,
        isDirty: stockout ? true : currentRow.isDirty,
      };

      setFormData((prev) => ({ ...prev, stockItems: updatedItems }));
      return;
    }

    // lock stock selection during edit
    if (field === "stockId" && stockout) {
      return;
    }

    updatedItems[index] = {
      ...currentRow,
      [field]: value,
      isDirty: stockout ? true : currentRow.isDirty,
    };

    setFormData((prev) => ({ ...prev, stockItems: updatedItems }));
  };

  const addStockItem = () => {
    setFormData((prev) => ({
      ...prev,
      stockItems: [...prev.stockItems, createEmptyStockItem()],
    }));
  };

  const removeStockItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      stockItems: prev.stockItems.filter((_, i) => i !== index),
    }));
  };

  const getStockMeta = (item) => {
    const selectedStock = stockList.find((s) => s._id === item.stockId);
    // Stock-out reduces from Godown B
    const currentAvailable = Number(selectedStock?.stockBQuantity || 0);
    const currentAvailableA = Number(selectedStock?.stockAQuantity || 0);
    const originalQty = Number(item.originalQuantity || 0);
    const enteredQty = item.quantity === "" ? null : Number(item.quantity || 0);

    if (!item.stockId) {
      return {
        currentAvailable: 0,
        currentAvailableA: 0,
        remainingToShow: 0,
        maxAllowed: 0,
      };
    }

    // CREATE MODE
    if (!stockout) {
      return {
        currentAvailable,
        currentAvailableA,
        maxAllowed: currentAvailable,
        remainingToShow:
          enteredQty === null
            ? currentAvailable
            : Math.max(currentAvailable - enteredQty, 0),
      };
    }

    // EDIT MODE
    const maxAllowed = currentAvailable + originalQty;

    // if row not changed yet, show current available as-is
    const remainingToShow =
      item.isDirty && enteredQty !== null
        ? Math.max(currentAvailable + originalQty - enteredQty, 0)
        : currentAvailable;

    return {
      currentAvailable,
      currentAvailableA,
      maxAllowed,
      remainingToShow,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employeeId) {
      toast.error("Please select an employee.");
      return;
    }

    if (!formData.locationId) {
      toast.error("Please select a location.");
      return;
    }

    const validItems = formData.stockItems.filter(
      (item) => item.stockId && item.quantity && Number(item.quantity) > 0,
    );

    if (validItems.length === 0) {
      toast.error("Please add at least one valid stock item with quantity.");
      return;
    }

    for (const item of validItems) {
      const currentStock = stockList.find((s) => s._id === item.stockId);
      // Stock-out is from Godown B
      const currentAvailable = Number(currentStock?.stockBQuantity || 0);
      const originalQty = Number(item.originalQuantity || 0);

      const maxAllowed = stockout
        ? currentAvailable + originalQty
        : currentAvailable;

      if (Number(item.quantity) > maxAllowed) {
        toast.error(
          `Quantity for selected stock cannot exceed available stock in Godown B (${maxAllowed}).`,
        );
        return;
      }
    }

    setLoading(true);

    const payload = {
      employeeId: formData.employeeId,
      locationId: formData.locationId,
      date: formData.date,
      company: companyId,
      remarks: formData.remarks,
      stocks: validItems.map((item) => ({
        stock: item.stockId,
        quantity: Number(item.quantity),
      })),
    };

    try {
      if (stockout) {
        await axiosInstance.patch(
          `/logistics/stockout/${stockout._id}`,
          payload,
        );
      } else {
        await axiosInstance.post("/logistics/stockout", {
          ...payload,
          createdBy: currentUserId,
        });
      }

      toast.success(
        stockout
          ? "Stock Out updated successfully"
          : "Stock Out created successfully",
      );
      onClose();
    } catch (error) {
      let errorMessage = "Error saving stock out";
      if (error.response?.data?.message) {
        errorMessage = Array.isArray(error.response.data.message)
          ? error.response.data.message.join(", ")
          : error.response.data.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content loan_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">
              {stockout ? "Edit Stock Out" : "Create Stock Out"}
            </h5>
            <button
              type="button"
              className="btn-close text-white"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body vesel_form">
            <form onSubmit={handleSubmit}>
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Basic Details</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3 position-relative">
                      <label className="form-label">Employee</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type employee name or code"
                        value={
                          empSearch ||
                          (formData.employeeId
                            ? employees.find(
                                (e) => e._id === formData.employeeId,
                              )?.name || ""
                            : "")
                        }
                        onChange={(e) => {
                          setEmpSearch(e.target.value);
                          setFormData((prev) => ({ ...prev, employeeId: "" }));
                        }}
                        onFocus={() => {
                          closeAllDropdowns();
                          setShowEmpDropdown(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowEmpDropdown(false), 150);
                        }}
                        required
                      />
                      {showEmpDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 200,
                            overflowY: "auto",
                          }}
                        >
                          {employees
                            .filter(
                              (item) =>
                                (item.name || "")
                                  .toLowerCase()
                                  .includes(empSearch.toLowerCase()) ||
                                (item.employeeNo
                                  ? item.employeeNo.toString()
                                  : ""
                                ).includes(empSearch) ||
                                (item.companyId?.company_name || "")
                                  .toLowerCase()
                                  .includes(empSearch.toLowerCase()),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    employeeId: item._id,
                                  }));
                                  setEmpSearch(item.name);
                                  setShowEmpDropdown(false);
                                }}
                              >
                                {`${item.name} - ${item.employeeNo}${item.companyId?.company_name ? ` (${item.companyId.company_name})` : ""}`}
                              </li>
                            ))}
                          {employees.filter(
                            (item) =>
                              (item.name || "")
                                .toLowerCase()
                                .includes(empSearch.toLowerCase()) ||
                              (item.employeeNo
                                ? item.employeeNo.toString()
                                : ""
                              ).includes(empSearch) ||
                              (item.companyId?.company_name || "")
                                .toLowerCase()
                                .includes(empSearch.toLowerCase()),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>

                    <div className="col-md-6 mb-3  position-relative">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type location code or name"
                        value={locSearch}
                        onChange={(e) => {
                          setLocSearch(e.target.value);
                          setFormData((prev) => ({ ...prev, locationId: "" }));
                        }}
                        onFocus={() => {
                          closeAllDropdowns();
                          setShowLocDropdown(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowLocDropdown(false), 150);
                        }}
                        required
                      />
                      {showLocDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 200,
                            overflowY: "auto",
                          }}
                        >
                          {location
                            .filter(
                              (item) =>
                                (item.code || "")
                                  .toLowerCase()
                                  .includes(locSearch.toLowerCase()) ||
                                (item.yard_name || "")
                                  .toLowerCase()
                                  .includes(locSearch.toLowerCase()),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    locationId: item._id,
                                  }));
                                  setLocSearch(
                                    item.code + " - " + item.yard_name,
                                  );
                                  setShowLocDropdown(false);
                                }}
                              >
                                {`${item.code} - ${item.yard_name}`}
                              </li>
                            ))}
                          {location.filter(
                            (item) =>
                              (item.code || "")
                                .toLowerCase()
                                .includes(locSearch.toLowerCase()) ||
                              (item.yard_name || "")
                                .toLowerCase()
                                .includes(locSearch.toLowerCase()),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="card shadow-sm mb-4 rounded-3"
                style={{ position: "unset" }}
              >
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Stock Items</h5>

                  {formData.stockItems.map((item, index) => {
                    const {
                      currentAvailable,
                      currentAvailableA,
                      remainingToShow,
                    } = getStockMeta(item);

                    return (
                      <div className="row mb-3 align-items-start" key={index}>
                        <div className="col-md-6 mb-2 position-relative">
                          <label className="form-label">Stock</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type stock name or number"
                            value={
                              stockSearch ||
                              (item.stockId
                                ? stockList.find((s) => s._id === item.stockId)
                                    ?.name +
                                  " - " +
                                  stockList.find((s) => s._id === item.stockId)
                                    ?.stockNo
                                : "")
                            }
                            onChange={(e) => {
                              if (!stockout) {
                                setStockSearch(e.target.value);
                                handleStockItemChange(index, "stockId", "");
                              }
                            }}
                            onFocus={() => {
                              if (!stockout) {
                                closeAllDropdowns();
                                setShowStockDropdown((prev) => ({
                                  ...prev,
                                  [index]: true,
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTimeout(
                                () =>
                                  setShowStockDropdown((prev) => ({
                                    ...prev,
                                    [index]: false,
                                  })),
                                150,
                              );
                            }}
                            required
                            disabled={!!stockout}
                          />
                          {showStockDropdown[index] && !stockout && (
                            <ul
                              className="list-group position-absolute"
                              style={{
                                zIndex: 1200,
                                width: "100%",
                                maxHeight: 200,
                                overflowY: "auto",
                              }}
                            >
                              {stockList
                                .filter(
                                  (stock) =>
                                    (stock.name || "")
                                      .toLowerCase()
                                      .includes(stockSearch.toLowerCase()) ||
                                    (stock.stockNo
                                      ? stock.stockNo.toString()
                                      : ""
                                    ).includes(stockSearch),
                                )
                                .map((stock) => (
                                  <li
                                    key={stock._id}
                                    className="list-group-item list-group-item-action"
                                    style={{ cursor: "pointer" }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      handleStockItemChange(
                                        index,
                                        "stockId",
                                        stock._id,
                                      );
                                      setStockSearch("");
                                      setShowStockDropdown((prev) => ({
                                        ...prev,
                                        [index]: false,
                                      }));
                                    }}
                                  >
                                    {`${stock.name} - ${stock.stockNo}`}
                                  </li>
                                ))}
                              {stockList.filter(
                                (stock) =>
                                  (stock.name || "")
                                    .toLowerCase()
                                    .includes(stockSearch.toLowerCase()) ||
                                  (stock.stockNo
                                    ? stock.stockNo.toString()
                                    : ""
                                  ).includes(stockSearch),
                              ).length === 0 && (
                                <li className="list-group-item list-group-item-action">
                                  No results found
                                </li>
                              )}
                            </ul>
                          )}

                          {item.stockId && (
                            <div className="mt-1">
                              <small className="text-primary d-block">
                                Available (Godown A): {currentAvailableA}
                              </small>
                              <small className="text-success d-block">
                                Available (Godown B): {currentAvailable}
                              </small>
                            </div>
                          )}
                        </div>

                        <div className="col-md-4 mb-2">
                          <label className="form-label">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            value={item.quantity}
                            onChange={(e) =>
                              handleStockItemChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            required
                            min={1}
                          />
                          <small className="text-success d-block mt-1">
                            Remaining (Godown B):{" "}
                            {item.stockId ? remainingToShow : 0}
                          </small>
                        </div>

                        <div className="col-md-2 d-flex align-items-end justify-content-start">
                          {formData.stockItems.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeStockItem(index)}
                            >
                              <i className="bi bi-trash3"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {!stockout && (
                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={addStockItem}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        <span>Add</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="card shadow-sm mb-4 rounded-3"
                style={{ position: "unset" }}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        rows="2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading
                    ? stockout
                      ? "Updating"
                      : "Creating"
                    : stockout
                      ? "Update Stock Out"
                      : "Create Stock Out"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockOutForm;
